"use client"
import { useMemo, type ReactNode } from "react"
import { COMPARISON_PLAN_ORDER, METRIC_DETAILS, SUPPLEMENT_SLUGS, SUPPLEMENT_TITLES } from "../metricDetailsData"
import { PLAN_BRANCH_SERIES } from "../planBranchSeries"
import { METRIC_LABELS } from "../metricLabels"
import {
  MetricScenarioChart,
  metricNumericValueAtYear,
  metricValueAtYear,
  type ComparisonBranch,
} from "../MetricScenarioChart"
import { MetricBarChart } from "../MetricBarChart"
import { buildScenarioSeriesFromModel } from "../buildModelSeries"
import { usePowerShareSeries } from "../powerShareSeries"
import { useMetricDetails } from "../metricDetailsContext"
import type { BranchKey, MetricId } from "../metricDetailsTypes"
import { EmploymentRate } from "../../../indicators/EmploymentRate"
import { MedianIncome } from "../../../indicators/MedianIncome"
import { AlignmentResearcherCount } from "../../../indicators/AlignmentResearcherCount"
import { SafetyTaxIndicator } from "../../../indicators/SafetyTaxIndicator"
import { DistributionOfPowerBubbles4Fade } from "../../blocks/power/DistributionOfPowerBubbles4Fade"
import { METRIC_HOVER_DEFINITIONS } from "../../blocks/metrics/metricHoverSwap"
import { getCapabilityThresholdLabelForValue } from "../../../extrapolated-graph/capabilityThresholds"

// ─── Editable content ────────────────────────────────────────────────────────
// One-line layperson definition per metric, shown beside the clicked HUD block
// at the top of every panel redesign. The four stat metrics reuse the
// in-canvas hover copy (METRIC_HOVER_DEFINITIONS) so the two never drift; the
// rest are panel-only.
export const METRIC_PANEL_DEFINITIONS: Record<MetricId, { title: string; definition: ReactNode }> = {
  employment: METRIC_HOVER_DEFINITIONS.employment,
  alignment: METRIC_HOVER_DEFINITIONS.alignmentEffort,
  income: METRIC_HOVER_DEFINITIONS.medianIncome,
  safetyTax: METRIC_HOVER_DEFINITIONS.safetyTax,
  capability: {
    title: "AI Capabilities",
    definition:
      "How much faster AI research moves with AI assistance than with humans alone (1× = human-only speed).",
  },
  power: {
    title: "Distribution of Power",
    definition:
      "The largest single holder’s share of world frontier compute — a falling share means power is spreading out.",
  },
  labor: {
    title: "Machine Labor",
    definition: "AI & robot workers measured in human-equivalent units of labor.",
  },
}

// Label on the live zone's pulsing readout, and the reference zone's headers,
// shared so the five designs speak one vocabulary.
export const PANEL_LIVE_LABEL = "LIVE"
export const PANEL_REFERENCE_SUPPLEMENTS_LEAD = "Explained more in"

// ─── Series plumbing ─────────────────────────────────────────────────────────
// Same series resolution as MetricDetailsContent: power samples the live
// power-compute data, the other live-sourced metrics come from the
// server-parsed model series, and anything left falls back to the metric's
// own CSV / capability series. comparisonBranches mirrors its construction
// there too — every branch drawn against Plan A comes from the model
// spreadsheet's per-plan series, anchored to the shared fork point.
export const useMetricPanelDetail = (metricId: MetricId) => {
  const { modelSeries } = useMetricDetails()
  const detail = METRIC_DETAILS[metricId]
  const powerSeries = usePowerShareSeries()
  const series = useMemo(() => {
    if (!detail) return null
    if (metricId === "power") return powerSeries
    const model = modelSeries[metricId]
    return model && model.planA.length > 0
      ? buildScenarioSeriesFromModel(model, {
          firstYear: detail.xTicks[0],
          observedEndYear: detail.xTicks[0] + 1,
          splitYear: detail.splitYear,
          lastYear: detail.xTicks[detail.xTicks.length - 1],
        })
      : detail.series
  }, [detail, metricId, modelSeries, powerSeries])
  const comparisonBranches = useMemo<ComparisonBranch[] | null>(() => {
    if (!series || !detail) return null
    const forkPoint = series.branches.A?.[0]
    return COMPARISON_PLAN_ORDER.map((id) => {
      const modeled = PLAN_BRANCH_SERIES[metricId]?.[id] ?? []
      const needsAnchor = forkPoint && modeled[0] && modeled[0].year > detail.splitYear + 0.01
      return {
        id,
        points: needsAnchor ? [forkPoint, ...modeled] : modeled,
        // Plan D keeps the chart's red (the default path is the one
        // comparison allowed to compete with Plan A); B / C / S draw as
        // hairline black dots (2026-07-09 — gray lines read as broken on
        // the cream canvas; thinness alone de-emphasizes).
        color: id === "D" ? undefined : "#111111",
      }
    })
  }, [series, detail, metricId])
  return { detail, series, comparisonBranches }
}

// The medium gray for everything Other-plans — the B / C / S comparison
// lines above and the Other-plans column headings. Born as tab 47's
// treatment, promoted to the baseline for ALL details panels 2026-07-07.
// The value is the chart's own muted ink (MUTED = #6b6b61 in
// MetricScenarioChart.tsx — the axis year numbers) blended 10% toward
// white: the same warm gray family as the chart chrome, but the 1.5px
// dashed lines ink heavier than 8.5px text, so they get a lighter cut.
export const OTHER_PLANS_GRAY = "#7a7a71"

// ─── Header pieces ───────────────────────────────────────────────────────────
// The back affordance every redesign leads with. Six treatments — "plain" is
// the base; tabs 11–15 try one alternative each (see the power-duo-back-*
// design ids in metricPanelRedesigns.tsx):
//   "plain"   — bare black arrow at the title's cap height, red on hover
//   "red"     — the same arrow in the piece's red, black on hover (color axis)
//   "chip"    — solid black square at the year pill's height, cream arrow,
//               red fill on hover (inversion axis — the metric-chip vocabulary)
//   "labeled" — the arrow plus a small mono uppercase BACK label, both
//               reddening together on hover (annotation axis)
//   "long"    — a long-tailed hairline SVG arrow at the HUD's 1.5px chart
//               stroke, red on hover (geometry axis)
//   "solid"   — a solid ◀ triangle marker, red on hover (weight/shape axis)
// The ← glyph inks far shorter than a capital at the same font size (~6px vs
// ~11.5px at 17px SF Mono, measured via canvas), so the bare-arrow designs set
// the arrow's font-size to 33px to draw exactly as tall as the title's "D" —
// while leading-[17px] keeps its line box at the title's height so the header
// row doesn't grow. The negative hit-area margins cancel the padding so the
// visible arrow hugs the title while the hit target stays comfortably larger.
export type PanelBackDesign =
  | "plain"
  | "red"
  | "chip"
  | "labeled"
  | "long"
  | "solid"
  | "outline-arrow"
  | PanelBackChipIcon

// Shared invisible hit-area recipe for the boxless designs.
const PANEL_BACK_HIT = "-m-2 flex shrink-0 items-center justify-center p-2 transition-colors"

// ─── Chip icon variants (tabs 17–21) ─────────────────────────────────────────
// Tab 12's chip with the icon redrawn as a true VECTOR instead of the ← text
// glyph: SVGs are blocks, so flex centering puts them dead-center in the chip
// (a text glyph rides its font baseline and sits visibly off-center). Stroke
// icons draw at the HUD's 1.5px chart stroke; edit the paths right here.
export type PanelBackChipIcon =
  | "chip-arrow"     // straight arrow, full head (the classic back glyph, redrawn)
  | "chip-chevron"   // a single ‹ chevron, no tail
  | "chip-chevrons"  // doubled ‹‹ chevrons (the "rewind" vocabulary)
  | "chip-return"    // return-key hook: drops from the title line, exits left
  | "chip-triangle"  // solid ◀ triangle, the piece's filled-marker vocabulary

const PANEL_BACK_CHIP_CLASS =
  "flex h-[23px] w-[23px] shrink-0 items-center justify-center rounded-[3px] bg-[#111111] text-[#fffff8] transition-colors hover:bg-[var(--accent)]"

const PANEL_BACK_CHIP_ICONS: Record<PanelBackChipIcon, ReactNode> = {
  "chip-arrow": (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
      <path d="M12 6.5H1.5M6 2L1.5 6.5L6 11" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  "chip-chevron": (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
      <path d="M8.5 1.5L3.5 6.5L8.5 11.5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  "chip-chevrons": (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
      <path d="M7 1.5L2 6.5L7 11.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M11.5 1.5L6.5 6.5L11.5 11.5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  "chip-return": (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
      <path d="M11.5 2.5V4A2.5 2.5 0 0 1 9 6.5H2M5 3.5L2 6.5L5 9.5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  "chip-triangle": (
    <svg width="13" height="13" viewBox="0 0 13 13" aria-hidden>
      <path d="M9.5 2L3 6.5L9.5 11Z" fill="currentColor" />
    </svg>
  ),
}

export const PanelBackButton = ({
  onClose,
  design = "plain",
  className,
}: {
  onClose?: () => void
  design?: PanelBackDesign
  className?: string
}) => {
  const shared = {
    type: "button" as const,
    onClick: onClose,
    "aria-label": "Back to HUD",
  }
  if (design in PANEL_BACK_CHIP_ICONS) {
    return (
      <button {...shared} className={`${PANEL_BACK_CHIP_CLASS} ${className ?? ""}`}>
        {PANEL_BACK_CHIP_ICONS[design as PanelBackChipIcon]}
      </button>
    )
  }
  // The minimal panel's back (2026-07-09): the chip redrawn in the hairline
  // vocabulary of the supplement-link chips — outlined square, black ← vector,
  // border and icon reddening together on hover — so the header speaks one
  // chip language instead of mixing solid and outline.
  if (design === "outline-arrow") {
    return (
      <button
        {...shared}
        className={`flex h-[23px] w-[23px] shrink-0 items-center justify-center rounded-[3px] border border-[#111111] text-[#111111] transition-colors hover:border-[var(--accent)] hover:text-[var(--accent)] ${className ?? ""}`}
      >
        {PANEL_BACK_CHIP_ICONS["chip-arrow"]}
      </button>
    )
  }
  switch (design) {
    case "red":
      return (
        <button
          {...shared}
          className={`${PANEL_BACK_HIT} font-mono text-[33px] font-bold leading-[17px] text-[var(--accent)] hover:text-black ${className ?? ""}`}
        >
          ←
        </button>
      )
    case "chip":
      return (
        <button
          {...shared}
          className={`flex h-[23px] w-[23px] shrink-0 items-center justify-center rounded-[3px] bg-[#111111] font-mono text-[24px] font-bold leading-none text-[#fffff8] transition-colors hover:bg-[var(--accent)] ${className ?? ""}`}
        >
          ←
        </button>
      )
    case "labeled":
      return (
        <button
          {...shared}
          className={`${PANEL_BACK_HIT} gap-[6px] font-mono font-bold text-black hover:text-[var(--accent)] ${className ?? ""}`}
        >
          <span className="text-[33px] leading-[17px]">←</span>
          <span className="text-[8.5px] uppercase leading-none tracking-wider">Back</span>
        </button>
      )
    case "long":
      return (
        <button
          {...shared}
          className={`${PANEL_BACK_HIT} text-black hover:text-[var(--accent)] ${className ?? ""}`}
        >
          <svg width="32" height="13" viewBox="0 0 32 13" fill="none" aria-hidden>
            <path d="M31 6.5H1.75M7.5 1L1.75 6.5L7.5 12" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </button>
      )
    case "solid":
      return (
        <button
          {...shared}
          className={`${PANEL_BACK_HIT} font-mono text-[16px] font-bold leading-[17px] text-black hover:text-[var(--accent)] ${className ?? ""}`}
        >
          ◀
        </button>
      )
    default:
      return (
        <button
          {...shared}
          className={`${PANEL_BACK_HIT} font-mono text-[33px] font-bold leading-[17px] text-black hover:text-[var(--accent)] ${className ?? ""}`}
        >
          ←
        </button>
      )
  }
}

// The current story year, at the panel's header tier: 17px mono bold,
// 3px/11px padding, 8px radius, no border — larger than the graph pill's
// 14px title tier (see the data-hud-year-pill element in
// ExtrapolatedGraphRenderer.tsx). The pill's flight into the panel
// (DetailsYearPill) morphs font-size/padding/radius mid-glide and lands
// exactly on this box, so there's still no crossfade artifact at landing —
// keep DetailsYearPill's PANEL_TIER in sync with this styling.
export const PanelYearReadout = ({
  year,
  className,
}: {
  year: number
  className?: string
}) => (
  // data-metric-panel-year: landing slot for the year pill's flight
  // (FlyingPanelElements) — the graph's pill travels here on open.
  <span
    data-metric-panel-year
    className={`inline-block rounded-[8px] bg-[#fffff8] px-[11px] py-[3px] font-mono text-[17px] font-bold text-black tabular-nums ${className ?? ""}`}
  >
    {Math.floor(year)}
  </span>
)

// The metric title — carries data-metric-details-title so the flying metric
// label (FlyingMetricLabel) lands exactly on it. `suffix` appends to the
// metric name (e.g. " details" → "Distribution of Power details"). Natural
// case, sized to the year pill's 17px (PanelYearReadout) so the header row
// reads as one weight.
export const PanelTitle = ({
  metricId,
  suffix = "",
  // Replaces the metric label outright (e.g. tab 42's "Data behind…" title).
  text,
  className,
}: {
  metricId: MetricId
  suffix?: string
  text?: string
  className?: string
}) => (
  <h2
    data-metric-details-title
    className={`m-0 font-mono text-[17px] font-bold leading-none tracking-[0.04em] ${className ?? ""}`}
  >
    {text ?? (
      <>
        {METRIC_LABELS[metricId]}
        {suffix}
      </>
    )}
  </h2>
)

// ─── The clicked HUD block, re-rendered at the panel top ─────────────────────
// Live big-number stat for the metrics whose HUD block is the graph or the
// labor strip (no compact block to re-render).
const MetricBigStat = ({ metricId, year }: { metricId: MetricId; year: number }) => {
  const { detail, series } = useMetricPanelDetail(metricId)
  if (!detail || !series) return null
  const nowValue = metricValueAtYear(series, detail.axis, year)
  const numeric = metricNumericValueAtYear(series, detail.axis, year)
  const levelLabel =
    metricId === "capability" && numeric !== null
      ? getCapabilityThresholdLabelForValue(numeric)
      : null
  return (
    <div>
      <div className="font-mono text-[24px] font-bold leading-none tabular-nums">{nowValue}</div>
      {levelLabel && (
        <div className="mt-1 font-mono text-[9.5px] font-bold uppercase tracking-wide">
          {levelLabel}
        </div>
      )}
    </div>
  )
}

// Per-metric renderer for the block the reader clicked, live against the
// story year. pointer-events-none in the wrapper below keeps them display-only
// (no tooltips, no re-triggering the panel).
const METRIC_PANEL_LIVE_BLOCKS: Record<MetricId, (year: number) => ReactNode> = {
  employment: () => <EmploymentRate valueFirst label="Employment" labelFontSize="9px" />,
  income: () => <MedianIncome valueFirst label="Median Income" labelFontSize="9px" />,
  alignment: () => (
    <AlignmentResearcherCount valueFirst label="Alignment Effort" units="" labelFontSize="9px" />
  ),
  safetyTax: () => (
    <SafetyTaxIndicator valueFirst label="Safety Tax" labelFontSize="9px" />
  ),
  power: () => (
    <DistributionOfPowerBubbles4Fade
      className="w-[190px]"
      height={104}
      strokeColor="#000"
      strokeWidth={1.5}
      iconColor="#000"
      backgroundColor="transparent"
      maxWidth={190}
      gapPx={2}
      borderRadiusPx={3}
    />
  ),
  capability: (year) => <MetricBigStat metricId="capability" year={year} />,
  labor: (year) => <MetricBigStat metricId="labor" year={year} />,
}

// Wide treatment for the sketch layouts: the treemap lands as the panel's
// hero, spanning (most of) the column. Metrics without a wide rendering fall
// back to their compact block.
const METRIC_PANEL_LIVE_BLOCKS_WIDE: Partial<Record<MetricId, (year: number) => ReactNode>> = {
  power: () => (
    <DistributionOfPowerBubbles4Fade
      className="w-full"
      height={150}
      strokeColor="#000"
      strokeWidth={1.5}
      iconColor="#000"
      backgroundColor="transparent"
      // Caps at the full HUD width; the container (433px in the inset
      // frames, 455px in flush ones) is what actually sets the size.
      maxWidth={455}
      gapPx={2}
      borderRadiusPx={3}
    />
  ),
}

// Full-bleed treatment (power-stack panels, tabs 22–26): the treemap expands
// to the HUD's full width but KEEPS its on-HUD 120px height — a pure
// horizontal stretch, so the panel stays short enough to fit above a ~725px
// fold.
const METRIC_PANEL_LIVE_BLOCKS_FULL: Partial<Record<MetricId, (year: number) => ReactNode>> = {
  power: () => (
    <DistributionOfPowerBubbles4Fade
      className="w-full"
      height={120}
      strokeColor="#000"
      strokeWidth={1.5}
      iconColor="#000"
      backgroundColor="transparent"
      maxWidth={455}
      gapPx={2}
      borderRadiusPx={3}
    />
  ),
}

// HUD-identical treatment (power-duo panel): exactly the treemap's on-HUD
// geometry (220×120), so its flight into the panel is a pure move with no
// rescale.
const METRIC_PANEL_LIVE_BLOCKS_HUD_SIZE: Partial<Record<MetricId, (year: number) => ReactNode>> = {
  power: () => (
    <DistributionOfPowerBubbles4Fade
      className="w-[220px]"
      height={120}
      strokeColor="#000"
      strokeWidth={1.5}
      iconColor="#000"
      backgroundColor="transparent"
      maxWidth={220}
      gapPx={2}
      borderRadiusPx={3}
    />
  ),
}

export const PanelClickedBlock = ({
  metricId,
  year,
  size = "compact",
  className,
}: {
  metricId: MetricId
  year: number
  // "wide": hero width for the sketch layouts; "hud": the block's on-HUD
  // geometry (pure-move flight); "full": full HUD width at on-HUD height
  // (pure horizontal stretch). Metrics without the requested rendering
  // fall back to their compact block.
  size?: "compact" | "wide" | "hud" | "full"
  className?: string
}) => {
  const renderers = {
    compact: METRIC_PANEL_LIVE_BLOCKS[metricId],
    wide: METRIC_PANEL_LIVE_BLOCKS_WIDE[metricId] ?? METRIC_PANEL_LIVE_BLOCKS[metricId],
    hud: METRIC_PANEL_LIVE_BLOCKS_HUD_SIZE[metricId] ?? METRIC_PANEL_LIVE_BLOCKS[metricId],
    full: METRIC_PANEL_LIVE_BLOCKS_FULL[metricId] ?? METRIC_PANEL_LIVE_BLOCKS[metricId],
  }
  return (
    // data-metric-panel-block: landing slot for the clicked block's flight
    // (FlyingPanelElements) — the HUD element travels here on open.
    <div data-metric-panel-block className={`pointer-events-none ${className ?? ""}`}>
      {renderers[size](year)}
    </div>
  )
}

// The hover-detail copy that sits beside the clicked block.
export const PanelDefinition = ({
  metricId,
  className,
}: {
  metricId: MetricId
  className?: string
}) => (
  <div
    className={`font-sans text-[10.5px] leading-[1.45] ${className ?? ""}`}
  >
    {METRIC_PANEL_DEFINITIONS[metricId].definition}
  </div>
)

// ─── Live section pieces ─────────────────────────────────────────────────────
export const PanelLiveValue = ({ metricId, year }: { metricId: MetricId; year: number }) => {
  const { detail, series } = useMetricPanelDetail(metricId)
  if (!detail || !series) return null
  return (
    <span className="font-mono text-[15px] font-bold leading-none tabular-nums">
      {metricValueAtYear(series, detail.axis, year)}
    </span>
  )
}

export const PanelChart = ({
  metricId,
  year,
  viewWidth,
  bottomCropPx,
  barChartWithCaption = true,
}: {
  metricId: MetricId
  year: number
  // Half-column placements pass the container's px width so the chart
  // compresses its plot instead of scaling its labels down (see
  // MetricScenarioChart's viewWidth).
  viewWidth?: number
  // Shaves px off the chart's bottom (plot floor + year labels ride up) —
  // see MetricScenarioChart's bottomCropPx.
  bottomCropPx?: number
  // Safety Tax's bar chart carries its own title caption by default; a host
  // that already prints that title as a section header turns it off.
  barChartWithCaption?: boolean
}) => {
  const { detail, series, comparisonBranches } = useMetricPanelDetail(metricId)
  if (!detail || !series || !comparisonBranches) return null
  // Safety Tax is cumulative: its trajectory zone compares the total slowdown
  // each plan accrues by its hand-off as a bar-per-plan chart rather than the
  // over-time lines.
  if (metricId === "safetyTax") {
    return (
      <MetricBarChart
        series={series}
        comparisonBranches={comparisonBranches}
        axis={detail.axis}
        endLabels={detail.endLabels}
        showCaption={barChartWithCaption}
      />
    )
  }
  return (
    <MetricScenarioChart
      series={series}
      axis={detail.axis}
      endLabels={detail.endLabels}
      splitYear={detail.splitYear}
      year={year}
      comparisonBranches={comparisonBranches}
      viewWidth={viewWidth}
      bottomCropPx={bottomCropPx}
    />
  )
}

// The pulsing "LIVE — value" readout line marking the updating zone.
export const PanelLiveTicker = ({
  metricId,
  year,
  className,
}: {
  metricId: MetricId
  year: number
  className?: string
}) => (
  <div className={`flex items-baseline gap-2 ${className ?? ""}`}>
    <span
      className="inline-block h-[7px] w-[7px] animate-pulse self-center rounded-full"
      style={{ background: "var(--accent)" }}
      aria-hidden
    />
    <span className="font-mono text-[10px] font-bold uppercase tracking-wider">
      {PANEL_LIVE_LABEL}
    </span>
    <PanelLiveValue metricId={metricId} year={year} />
  </div>
)

// ─── Reference (definitions) section pieces ──────────────────────────────────
// Scenario-row swatches mirror the chart lines; tone-aware so the dark
// (dossier) reference zone stays legible.
const SCENARIO_TONES = {
  light: { planA: "#111111", defaultPath: "var(--race-background)", text: "#111111" },
  dark: { planA: "#fffff8", defaultPath: "#e08a7d", text: "#fffff8" },
}

const SwBranch = ({ branch, tone }: { branch: BranchKey; tone: keyof typeof SCENARIO_TONES }) => {
  const colors = SCENARIO_TONES[tone]
  return branch === "A" ? (
    <svg width="18" height="8" viewBox="0 0 18 8" className="inline-block align-middle" aria-hidden>
      <line x1="0" y1="4" x2="8" y2="4" stroke={colors.planA} strokeWidth="1.5" />
      <line x1="9" y1="4" x2="18" y2="4" stroke={colors.planA} strokeWidth="1.5" strokeDasharray="4 3" />
    </svg>
  ) : (
    <svg width="18" height="8" viewBox="0 0 18 8" className="inline-block align-middle" aria-hidden>
      <line x1="0" y1="4" x2="18" y2="4" stroke={colors.defaultPath} strokeWidth="1.5" strokeDasharray="4 3" />
    </svg>
  )
}

// Supplement links — every design places this row at the TOP of its reference
// zone, before the prose.
export const PanelSupplementLinks = ({
  metricId,
  tone = "light",
  asChips = false,
  className,
}: {
  metricId: MetricId
  tone?: keyof typeof SCENARIO_TONES
  // Chip presentation (bordered pills) for the ledger design; default is the
  // inline "Explained more in …" sentence.
  asChips?: boolean
  className?: string
}) => {
  const detail = METRIC_DETAILS[metricId]
  const slugs = detail?.sources.filter((s) => SUPPLEMENT_SLUGS.has(s)) ?? []
  if (slugs.length === 0) return null
  const linkColor = tone === "dark" ? "#9fd6b2" : "#2A623D"
  if (asChips) {
    return (
      <div className={`flex flex-wrap gap-1.5 ${className ?? ""}`}>
        {slugs.map((slug) => (
          <a
            key={slug}
            href={`/supplements/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full border px-2 py-0.5 font-mono text-[9.5px] no-underline transition-colors hover:text-inherit"
            style={{ borderColor: linkColor, color: linkColor }}
          >
            {SUPPLEMENT_TITLES[slug] ?? slug} ↗
          </a>
        ))}
      </div>
    )
  }
  return (
    <div className={`font-mono text-[10px] ${className ?? ""}`} style={{ opacity: tone === "dark" ? 0.9 : undefined }}>
      {PANEL_REFERENCE_SUPPLEMENTS_LEAD}{" "}
      {slugs.map((slug, i) => (
        <span key={slug}>
          {i > 0 && (i === slugs.length - 1 ? " and " : ", ")}
          <a
            href={`/supplements/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2"
            style={{ color: linkColor, textDecorationColor: `${tone === "dark" ? "#9fd6b2" : "#2A623D"}55` }}
          >
            {SUPPLEMENT_TITLES[slug] ?? slug}
          </a>
        </span>
      ))}
    </div>
  )
}

// The definition prose: the metric's explanation, then the two scenario
// paragraphs. Static reference copy — nothing in here updates with the year.
export const PanelReferenceProse = ({
  metricId,
  tone = "light",
  className,
}: {
  metricId: MetricId
  tone?: keyof typeof SCENARIO_TONES
  className?: string
}) => {
  const detail = METRIC_DETAILS[metricId]
  if (!detail) return null
  const colors = SCENARIO_TONES[tone]
  return (
    <div className={className} style={{ color: colors.text }}>
      {detail.explanation && (
        <div className="font-system-ui text-[13.5px] leading-normal [&_b]:font-bold [&_em]:italic [&_li]:mb-1.5 [&_p]:mt-0 [&_p]:mb-[10px] [&_p:last-child]:mb-0 [&_ul]:mt-0 [&_ul]:mb-[10px] [&_ul]:list-disc [&_ul]:pl-5">
          {detail.explanation}
        </div>
      )}
      <ul className="mt-4 grid list-none gap-2.5 p-0">
        {([["D", "Default path", colors.defaultPath], ["A", "Plan A", colors.planA]] as const).map(
          ([branch, name, nameColor]) => (
            <li key={branch} className="grid grid-cols-[22px_1fr] items-baseline gap-x-2.5 gap-y-0.5">
              <span className="mt-1 justify-self-start" aria-hidden>
                <SwBranch branch={branch} tone={tone} />
              </span>
              <b className="font-mono text-[10px] font-bold uppercase tracking-wider" style={{ color: nameColor }}>
                {name}
              </b>
              <span className="col-start-1 col-span-2 font-system-ui text-[13.5px] leading-normal [&_a]:break-words [&_b]:font-bold">
                {detail.scenarios[branch]}
              </span>
            </li>
          ),
        )}
      </ul>
    </div>
  )
}
